import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, map, startWith, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { PipelineInterface } from '@vendasta/sales-opportunities';
import { CRMPipelineSelectorService } from '../../../../../../../../data-services/crm-pipeline-selector.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutomationReplacementChipsComponent } from '../../../../../../../../common/components';
import { DynamicOptionsComponent } from '../../../../dynamic-options';
import { StaticSelectorService } from '../../../../../../../../data-services/static-selector.service';
import { distinctUntilChanged, filter } from 'rxjs/operators';

export const ANY_STAGE = '*';
export const FIRST_STAGE = 'FIRST_STAGE';
export const NONE = 'NONE';

export type DefaultStageSelection = 'NONE' | 'FIRST_STAGE' | '*';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, AutomationReplacementChipsComponent],
  selector: 'automata-crm-pipeline-selector',
  templateUrl: './pipeline-selector.component.html',
})
export class PipelineSelectorComponent implements OnInit, DynamicOptionsComponent {
  private destroyRef = inject(DestroyRef);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly translateService = inject(TranslateService);
  protected readonly pipelineService = inject(CRMPipelineSelectorService);
  private initialValuesSet = false;

  @Input() required = true;
  @Input() includeAnyStageOption = false;
  @Input() showStageSelection = true;
  @Input() defaultStageSelection: DefaultStageSelection = 'NONE';

  // filter interface
  @Input() initialValue: any[] = [];
  @Output() loaded: EventEmitter<null> = new EventEmitter<null>();

  pipelineControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  stageControl = new FormControl<string | null>(null, {
    validators: this.showStageSelection ? [Validators.required] : [],
  });

  stageOptions: Map<string, string> = new Map<string, string>();
  stageOptionsDataService = new StaticSelectorService<string>(this.stageOptions, this.translateService);

  loadingValues = signal<boolean>(true);
  selectedValue$ = this.pipelineControl.valueChanges;
  pipelines$ = this.pipelineService.doLookup('', '', 100).pipe(map((pagedResponse) => pagedResponse.results ?? []));

  ngOnInit(): void {
    const pipelineChanges$ = this.pipelineControl.valueChanges.pipe(
      startWith(this.pipelineControl.value),
      distinctUntilChanged(),
    );

    this.pipelineControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((c) => !c),
      )
      .subscribe((_) => this.clearStageOptions());

    //Update the stage options
    combineLatest([pipelineChanges$, this.pipelines$])
      .pipe(
        tap(() => this.loadingValues.set(true)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([pipelineId, pipelines]) => {
        this.stageOptions.clear();
        this.stageControl.reset();
        if (pipelineId ?? '' !== '') {
          this.assignStageOptions(pipelineId, pipelines);
          if (!this.initialValuesSet && this.initialValue[1]) {
            this.setStageSelection(this.initialValue[1]);
          } else {
            this.setDefaultStageSelection(this.defaultStageSelection);
          }
        }
        this.initialValuesSet = true;
        this.cdr.detectChanges();
        this.loadingValues.set(false);
      });

    this.pipelineControl.setValue(this.initialValue[0]);
  }

  private setDefaultStageSelection(stageSelection: DefaultStageSelection) {
    if (this.includeAnyStageOption && stageSelection === ANY_STAGE) {
      this.stageControl.setValue(ANY_STAGE);
    }

    if (this.stageOptions.size > 0 && stageSelection === FIRST_STAGE) {
      this.stageControl.setValue(this.stageOptions.keys().next().value);
    }
  }

  private setStageSelection(stageID: string): void {
    this.stageControl.setValue(stageID);
  }

  private assignStageOptions(pipelineId: string, pipelines: PipelineInterface[]): void {
    if (this.includeAnyStageOption) {
      this.stageOptions.set(
        ANY_STAGE,
        this.translateService.instant('AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.ANY_STAGE'),
      );
    }

    const pipeline = pipelines.find((p) => p.pipelineId === pipelineId);
    pipeline?.stages?.forEach((stageOption) => {
      this.stageOptions.set(stageOption.stageId || '', stageOption.name || '');
    });
  }

  private clearStageOptions(): void {
    this.stageOptions.clear();
  }

  finishedLoading(): void {
    this.loaded.emit();
  }

  getControlsToValidateOnSave(): FormControl[] {
    return [this.pipelineControl, this.stageControl];
  }

  isFormValid(): boolean {
    return !this.pipelineControl.invalid && !this.stageControl.invalid;
  }

  // filter interface
  getSelectedValue(): string[] {
    return [this.pipelineControl.value, this.stageControl?.value || ''];
  }
}
